import styled from "@emotion/styled";
import { 
  Button, 
  Typography, 
  Box,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { postApiServices } from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { ROUTE_PATHS } from "../../../routes/routePath";
import { errorToast, successToast } from "../../../utils/helper";
import PublicIcon from "@mui/icons-material/Public";
import LockResetIcon from "@mui/icons-material/LockReset";
import LoginIcon from "@mui/icons-material/Login";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

// Theme colors
const PRIMARY_COLOR = "#7285b3";
const PRIMARY_DARK = "#5a6a94";

// Styled Components
const PageContainer = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  backgroundColor: "#f5f6fa",
  padding: "20px",
});

const ResetCard = styled(Paper)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "40px",
  borderRadius: "16px",
  maxWidth: "450px",
  width: "100%",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
});

const FieldWrapper = styled(Box)({
  width: "100%",
  marginBottom: "16px",
});

const InputWrapper = styled(Box)({
  position: "relative",
  width: "100%",
});

const StyledInput = styled("input")(({ hasError }) => ({
  padding: "14px 50px 14px 20px",
  width: "100%",
  borderRadius: "12px",
  fontSize: "15px",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: hasError ? "#d32f2f" : "#e0e0e0",
  fontFamily: "Poppins, sans-serif",
  backgroundColor: "#fafafa",
  boxSizing: "border-box",
  transition: "all 0.3s ease",
  "&:focus": {
    outline: "none",
    borderColor: hasError ? "#d32f2f" : PRIMARY_COLOR,
    backgroundColor: "#fff",
    boxShadow: `0 0 0 3px rgba(114, 133, 179, 0.15)`,
  },
  "&:disabled": {
    backgroundColor: "#eee",
    cursor: "not-allowed",
  },
  "&::placeholder": {
    color: "#aaa",
  },
}));

const PasswordToggle = styled(IconButton)({
  position: "absolute",
  right: "8px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#999",
  "&:hover": {
    color: PRIMARY_COLOR,
  },
});

const StyledButton = styled(Button)({
  padding: "14px 20px",
  width: "100%",
  borderRadius: "12px",
  color: "white",
  fontSize: "15px",
  fontWeight: 600,
  backgroundColor: PRIMARY_COLOR,
  fontFamily: "Poppins, sans-serif",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: PRIMARY_DARK,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 12px rgba(114, 133, 179, 0.4)`,
  },
  "&:disabled": {
    backgroundColor: "#ccc",
  },
});

const BackButton = styled(Button)({
  padding: "10px 20px",
  borderRadius: "12px",
  fontSize: "14px",
  fontWeight: 500,
  fontFamily: "Poppins, sans-serif",
  textTransform: "none",
  color: "#666",
  borderColor: "#e0e0e0",
  "&:hover": {
    borderColor: PRIMARY_COLOR,
    color: PRIMARY_COLOR,
    backgroundColor: "rgba(114, 133, 179, 0.05)",
  },
});

const ErrorText = styled("span")({
  color: "#d32f2f",
  fontSize: "12px",
  marginTop: "4px",
  display: "block",
  paddingLeft: "4px",
});

const IconWrapper = styled(Box)({
  width: 80,
  height: 80,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 16,
  backgroundColor: "rgba(114, 133, 179, 0.1)",
});

const SuccessIconWrapper = styled(Box)({
  width: 80,
  height: 80,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 16,
  backgroundColor: "#e8f5e9",
});

const PasswordStrength = styled(Box)(({ strength }) => {
  const colors = {
    weak: "#f44336",
    medium: "#ff9800",
    strong: "#4caf50",
  };
  return {
    display: "flex",
    gap: "4px",
    marginTop: "8px",
    "& .bar": {
      height: "4px",
      flex: 1,
      borderRadius: "2px",
      backgroundColor: "#e0e0e0",
      transition: "background-color 0.3s ease",
    },
    "& .bar.active": {
      backgroundColor: colors[strength] || "#e0e0e0",
    },
  };
});

export const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");
  const tenantName = searchParams.get("tenant");
  const isNew = searchParams.get("isNew") === "true";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);

  const getPasswordStrength = (password) => {
    if (!password) return null;
    if (password.length < 6) return "weak";
    if (password.length < 10 || !/[A-Z]/.test(password) || !/[0-9]/.test(password)) return "medium";
    return "strong";
  };

  const passwordStrength = getPasswordStrength(newPassword);

  const validateField = (name, value) => {
    let error = "";
    
    switch (name) {
      case "newPassword":
        if (!value || value.trim() === "") {
          error = "New password is required";
        } else if (value.length < 6) {
          error = "Password must be at least 6 characters";
        }
        break;
      case "confirmPassword":
        if (!value || value.trim() === "") {
          error = "Please confirm your password";
        } else if (value !== newPassword) {
          error = "Passwords do not match";
        }
        break;
      default:
        break;
    }
    
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
    
    return error;
  };

  const handleBlur = (name) => {
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
    validateField(name, name === "newPassword" ? newPassword : confirmPassword);
  };

  const handlePasswordChange = (e) => {
    setNewPassword(e.target.value);
    if (errors.newPassword) {
      setErrors((prev) => ({ ...prev, newPassword: "" }));
    }
    // Also validate confirm password if it's been touched
    if (touched.confirmPassword && confirmPassword) {
      if (e.target.value !== confirmPassword) {
        setErrors((prev) => ({ ...prev, confirmPassword: "Passwords do not match" }));
      } else {
        setErrors((prev) => ({ ...prev, confirmPassword: "" }));
      }
    }
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    if (errors.confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: "" }));
    }
  };

  const validateAllFields = () => {
    let isValid = true;
    const newErrors = {};
    const newTouched = { newPassword: true, confirmPassword: true };

    if (!newPassword || newPassword.trim() === "") {
      newErrors.newPassword = "New password is required";
      isValid = false;
    } else if (newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters";
      isValid = false;
    }

    if (!confirmPassword || confirmPassword.trim() === "") {
      newErrors.confirmPassword = "Please confirm your password";
      isValid = false;
    } else if (confirmPassword !== newPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      isValid = false;
    }

    setTouched(newTouched);
    setErrors(newErrors);
    
    return isValid;
  };

  const resetPasswordMutation = useMutation({
    mutationFn: () =>
      postApiServices(apiRoutes.resetPassword, {
        token,
        tenantName,
        newPassword,
      }),
    onSuccess: () => {
      setIsSuccess(true);
      successToast("Password has been reset successfully!");
    },
    onError: (error) => {
      console.error("error", error);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data || 
                          "Something went wrong";
      errorToast(typeof errorMessage === "string" ? errorMessage : "Failed to reset password");
    },
  });

  const handleSubmit = () => {
    if (!token || !tenantName) {
      errorToast("Invalid or expired reset link");
      return;
    }

    const isValid = validateAllFields();
    
    if (!isValid) {
      errorToast("Please fill all required fields correctly");
      return;
    }
    
    resetPasswordMutation.mutate();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const handleBackToLogin = () => {
    navigate(ROUTE_PATHS.LOGIN);
  };

  // Check for invalid token
  if (!token || !tenantName) {
    return (
      <PageContainer>
        <ResetCard elevation={0}>
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <PublicIcon sx={{ color: PRIMARY_COLOR, fontSize: 45, mr: 1 }} />
            <Typography
              variant="h5"
              sx={{ color: PRIMARY_COLOR, fontWeight: 700, fontFamily: "Poppins" }}
            >
              Premierdis
            </Typography>
          </Box>

          <IconWrapper sx={{ backgroundColor: "#ffebee" }}>
            <LockResetIcon sx={{ fontSize: 40, color: "#f44336" }} />
          </IconWrapper>

          <Typography
            sx={{ 
              fontSize: "24px", 
              fontWeight: 600, 
              mb: 1, 
              fontFamily: "Poppins",
              color: "#333",
              textAlign: "center",
            }}
          >
            Invalid Reset Link
          </Typography>

          <Typography
            sx={{ 
              fontSize: "14px", 
              mb: 3, 
              fontFamily: "Poppins",
              color: "#666",
              textAlign: "center",
              lineHeight: 1.6,
            }}
          >
            This password reset link is invalid or has expired. Please request a new one.
          </Typography>

          <StyledButton 
            variant="contained" 
            onClick={() => navigate(ROUTE_PATHS.FORGOT_PASSWORD)}
            startIcon={<LockResetIcon />}
            sx={{ mb: 2 }}
          >
            Request New Link
          </StyledButton>

          <BackButton
            variant="outlined"
            onClick={handleBackToLogin}
            startIcon={<ArrowBackIcon />}
            fullWidth
          >
            Back to Login
          </BackButton>
        </ResetCard>
      </PageContainer>
    );
  }

  // Success View
  if (isSuccess) {
    return (
      <PageContainer>
        <ResetCard elevation={0}>
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <PublicIcon sx={{ color: PRIMARY_COLOR, fontSize: 45, mr: 1 }} />
            <Typography
              variant="h5"
              sx={{ color: PRIMARY_COLOR, fontWeight: 700, fontFamily: "Poppins" }}
            >
              Premierdis
            </Typography>
          </Box>

          <SuccessIconWrapper>
            <CheckCircleOutlineIcon sx={{ fontSize: 45, color: "#4caf50" }} />
          </SuccessIconWrapper>

          <Typography
            sx={{ 
              fontSize: "24px", 
              fontWeight: 600, 
              mb: 1, 
              fontFamily: "Poppins",
              color: "#333",
              textAlign: "center",
            }}
          >
            {isNew ? "Account Setup Successful!" : "Password Reset Successful!"}
          </Typography>

          <Typography
            sx={{ 
              fontSize: "14px", 
              mb: 3, 
              fontFamily: "Poppins",
              color: "#666",
              textAlign: "center",
              lineHeight: 1.6,
            }}
          >
            {isNew 
              ? "Your account has been successfully verified. You can now log in with your credentials."
              : "Your password has been successfully updated. You can now log in with your new password."
            }
          </Typography>

          <Alert 
            severity="success" 
            sx={{ 
              mb: 3, 
              width: "100%",
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontFamily: "Poppins" }}>
              For security reasons, please don't share your password with anyone.
            </Typography>
          </Alert>

          <StyledButton 
            variant="contained" 
            onClick={handleBackToLogin}
            startIcon={<LoginIcon />}
          >
            Continue to Login
          </StyledButton>
        </ResetCard>
      </PageContainer>
    );
  }

  // Form View
  return (
    <PageContainer>
      <ResetCard elevation={0}>
        {/* Logo */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <PublicIcon sx={{ color: PRIMARY_COLOR, fontSize: 45, mr: 1 }} />
          <Typography
            variant="h5"
            sx={{ color: PRIMARY_COLOR, fontWeight: 700, fontFamily: "Poppins" }}
          >
            Premierdis
          </Typography>
        </Box>

        <IconWrapper>
          <LockOutlinedIcon sx={{ fontSize: 40, color: PRIMARY_COLOR }} />
        </IconWrapper>

        <Typography
          sx={{ 
            fontSize: "24px", 
            fontWeight: 600, 
            mb: 0.5, 
            fontFamily: "Poppins",
            color: "#333",
          }}
        >
          {isNew ? "Verify & Set Password" : "Create New Password"}
        </Typography>

        <Typography
          sx={{ 
            fontSize: "14px", 
            mb: 3, 
            fontFamily: "Poppins",
            color: "#888",
            textAlign: "center",
          }}
        >
          {isNew
            ? "Please choose a strong password to complete your account setup."
            : "Your new password must be different from previously used passwords."
          }
        </Typography>

        {/* Form */}
        <Box sx={{ width: "100%" }}>
          {/* New Password */}
          <FieldWrapper>
            <InputWrapper>
              <StyledInput
                name="newPassword"
                type={showPassword ? "text" : "password"}
                placeholder="New Password *"
                value={newPassword}
                onChange={handlePasswordChange}
                onBlur={() => handleBlur("newPassword")}
                onKeyPress={handleKeyPress}
                hasError={touched.newPassword && errors.newPassword}
                disabled={resetPasswordMutation.isPending}
              />
              <PasswordToggle
                size="small"
                onClick={() => setShowPassword(!showPassword)}
                disabled={resetPasswordMutation.isPending}
              >
                {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
              </PasswordToggle>
            </InputWrapper>
            {touched.newPassword && errors.newPassword && (
              <ErrorText>{errors.newPassword}</ErrorText>
            )}
            {newPassword && !errors.newPassword && (
              <>
                <PasswordStrength strength={passwordStrength}>
                  <div className={`bar ${passwordStrength ? "active" : ""}`} />
                  <div className={`bar ${passwordStrength === "medium" || passwordStrength === "strong" ? "active" : ""}`} />
                  <div className={`bar ${passwordStrength === "strong" ? "active" : ""}`} />
                </PasswordStrength>
                <Typography 
                  sx={{ 
                    fontSize: "11px", 
                    mt: 0.5, 
                    color: passwordStrength === "weak" ? "#f44336" : passwordStrength === "medium" ? "#ff9800" : "#4caf50",
                    fontFamily: "Poppins",
                    textTransform: "capitalize",
                  }}
                >
                  {passwordStrength} password
                </Typography>
              </>
            )}
          </FieldWrapper>

          {/* Confirm Password */}
          <FieldWrapper>
            <InputWrapper>
              <StyledInput
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password *"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                onBlur={() => handleBlur("confirmPassword")}
                onKeyPress={handleKeyPress}
                hasError={touched.confirmPassword && errors.confirmPassword}
                disabled={resetPasswordMutation.isPending}
              />
              <PasswordToggle
                size="small"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={resetPasswordMutation.isPending}
              >
                {showConfirmPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
              </PasswordToggle>
            </InputWrapper>
            {touched.confirmPassword && errors.confirmPassword && (
              <ErrorText>{errors.confirmPassword}</ErrorText>
            )}
            {confirmPassword && !errors.confirmPassword && confirmPassword === newPassword && (
              <Typography 
                sx={{ 
                  fontSize: "11px", 
                  mt: 0.5, 
                  color: "#4caf50",
                  fontFamily: "Poppins",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                Passwords match
              </Typography>
            )}
          </FieldWrapper>

          {/* Submit Button */}
          <StyledButton 
            variant="contained" 
            onClick={handleSubmit}
            disabled={resetPasswordMutation.isPending}
            startIcon={!resetPasswordMutation.isPending && (isNew ? <LockOutlinedIcon /> : <LockResetIcon />)}
            sx={{ mt: 1 }}
          >
            {resetPasswordMutation.isPending ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                {isNew ? "Setting Password..." : "Resetting..."}
              </>
            ) : (
              isNew ? "Set Password" : "Reset Password"
            )}
          </StyledButton>
        </Box>

        {/* Divider */}
        <Box sx={{ width: "100%", my: 3, display: "flex", alignItems: "center" }}>
          <Divider sx={{ flexGrow: 1 }} />
        </Box>

        {/* Back to Login */}
        <BackButton
          variant="outlined"
          onClick={handleBackToLogin}
          startIcon={<ArrowBackIcon />}
          fullWidth
        >
          Back to Login
        </BackButton>
      </ResetCard>
    </PageContainer>
  );
};